/**
 * MultiSlideBuilder Agent - Tools Section
 * 
 * Standard tools section with preamble and postamble from Keywords AI.
 */

import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import type { ToolBase } from '../../../base/toolBase';
import { MULTI_SLIDE_BUILDER_PROMPT_KEYS } from './variables';

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS PREAMBLE
// ═══════════════════════════════════════════════════════════════════════════

class MultiSlideBuilderToolsPreambleSection extends PromptSectionBase {
    protected readonly keywordsKey = MULTI_SLIDE_BUILDER_PROMPT_KEYS.toolsPreamble;
    protected readonly fallbackContent = `You have access to several categories of tools to complete the build process.

Use these tools strategically to:
1. **Search** for reference slides that match the section's content needs
2. **Research** to gather data, statistics, and supporting information
3. **Create Excel data** for charts and tables that will be linked to slides
4. **Execute sections** by calling the ExecutionAgent with pre-defined areas
5. **Submit results** when all sections are built`;
}

const toolsPreambleSection = new MultiSlideBuilderToolsPreambleSection();

export function getToolsPreamble(): string {
    return toolsPreambleSection.getContent();
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS POSTAMBLE
// ═══════════════════════════════════════════════════════════════════════════

class MultiSlideBuilderToolsPostambleSection extends PromptSectionBase {
    protected readonly keywordsKey = MULTI_SLIDE_BUILDER_PROMPT_KEYS.toolsPostamble;
    protected readonly fallbackContent = `**Tool Usage Strategy:**
- Start each section with reference search (presentationLibrarySearchAgent)
- Gather research and Excel data before calling executeSection
- Pass context IDs (as_xxx, artifact_xxx, etc.) to executeSection
- Call submitResults only when ALL sections are attempted`;
}

const toolsPostambleSection = new MultiSlideBuilderToolsPostambleSection();

export function getToolsPostamble(): string {
    return toolsPostambleSection.getContent();
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS SECTION BUILDER
// ═══════════════════════════════════════════════════════════════════════════

export function buildToolsSection(tools: ToolBase[]): string {
    const parts: string[] = [];
    
    // 1. Preamble
    parts.push(getToolsPreamble());
    parts.push('');
    
    // 2. Individual tool descriptions
    for (const tool of tools) {
        parts.push(tool.systemPromptDescription);
        parts.push('');
    }
    
    // 3. Postamble
    parts.push(getToolsPostamble());
    
    return parts.join('\n');
}
